import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button, Dialog } from '../../ui';

/**
 * ConfirmDialog — confirmación (o aviso) del módulo Inventario con el Dialog del DS v2, en lugar
 * de window.confirm / window.alert (Fase 3c). Usa la forma recomendada del DS: `title` y `footer`
 * como props del Dialog.
 *
 * props:
 *   open, onClose
 *   titulo, children (el texto)
 *   onConfirm      async () => void. Mientras corre, los botones se bloquean; si lanza, el error se
 *                  enseña dentro del diálogo y el diálogo se queda abierto. Sin onConfirm es un AVISO:
 *                  sólo un botón «Cerrar».
 *   textoConfirmar texto del botón (por defecto «Confirmar»)
 *   destructivo    botón en rojo (variant danger)
 */
export default function ConfirmDialog({
  open,
  onClose,
  titulo,
  children,
  onConfirm,
  textoConfirmar = 'Confirmar',
  destructivo = false,
}) {
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setOcupado(false); setError(null); }
  }, [open]);

  const cerrar = () => { if (!ocupado) onClose(); };

  async function confirmar() {
    setOcupado(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'No se pudo completar.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={cerrar}
      title={titulo}
      size="sm"
      footer={onConfirm ? (
        <>
          <Button variant="secondary" onClick={cerrar} disabled={ocupado}>Cancelar</Button>
          <Button variant={destructivo ? 'danger' : undefined} onClick={confirmar} disabled={ocupado}>
            {ocupado && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {textoConfirmar}
          </Button>
        </>
      ) : (
        <Button variant="secondary" onClick={cerrar}>Cerrar</Button>
      )}
    >
      <div className="space-y-3 text-sm text-slate-700">
        {children}
        {error && (
          <div className="flex items-start gap-2 text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </Dialog>
  );
}
